import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ContentChildren, Directive, DoCheck, ElementRef, EmbeddedViewRef, EventEmitter, forwardRef, HostBinding, HostListener, inject, Inject, InjectionToken, Injector, Input, IterableDiffer, IterableDiffers, OnDestroy, Optional, Output, QueryList, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonComponent } from 'components/input/button/button.component';
import { IconComponent, IconType } from 'components/layout/icon/icon.component';
import { BrandModel } from 'models/generated/Entities.GormCrm.Brand';
import { TranslatePipe } from 'pipes/translate.pipe';
import { Subject, Subscription } from 'rxjs';
import { BrandService } from 'services/brand.service';
import { LanguageService } from 'services/language.service';

const ROW_OBJECT = new InjectionToken<object>('ListViewRowObject');
const IS_SORTED = new InjectionToken<Function>('ListViewRowIsSorted');

export class ListViewRowContext {
	$implicit!: object | null;
	index!: number | null;
	sorted: boolean = false;
	listData!: { objects?: object[]; sortedObjects?: object[]; }
}

@Component({
	selector: 'list-view > header',
	template: '<div class="header-delimiter"><ng-content></ng-content></div><em class="sort-icon" [ngClass]="getSortIcon()"></em>',
	imports: [CommonModule]
})
export class ListHeaderComponent {

	constructor(private listView: ListViewComponent, private elm: ElementRef<HTMLElement>) { }

	private _sortDirection = ListViewSorting.None;
	@Input() set defaultSort(defaultSort: 'ascending' | 'descending' | undefined) {
		this._sortDirection =
			defaultSort === 'ascending' ? ListViewSorting.Ascending :
				defaultSort === 'descending' ? ListViewSorting.Descending :
					ListViewSorting.None;

		if (this._sortDirection !== ListViewSorting.None) this.listView.setSortingColumn(this, false, this._sortDirection);
	}

	@HostBinding('class.sortable')
	@Input('sortColumn') sortable = true;

	@Input('filterColumn') filterable = true;

	private _filterValue?: string | number | Date;
	@Input()
	set filterValue(value: string | number | Date | undefined) {
		this._filterValue = value;
		this.setFilterColumn();
	}
	get filterValue() { return this._filterValue; }

	private _filterType?: FilterType;
	@Input()
	set filterType(value: FilterType | undefined) {
		this._filterType = value;
		this.setFilterColumn();
	}
	get filterType() { return this._filterType; }

	setFilterColumn() {
		if (this.filterValue !== undefined && this.filterType !== undefined) {
			this.listView.setFilterColumn(this, new ListViewFilterOption(this.filterType, this.filterValue));
		}
	}

	@HostBinding('class.filtered')
	get hasFilter() { return this.listView.filters.some(f => f.column == this); }

	@HostListener('click', ['$event'])
	onClick(event: MouseEvent) {
		if (!this.sortable) return;
		this.listView.setSortingColumn(this, event.ctrlKey || event.altKey);
	}

	mouseoverElement = false;

	@HostListener('mouseover')
	mouseOver() { this.mouseoverElement = true; }
	@HostListener('mouseout')
	mouseOut() { this.mouseoverElement = false; this.onMouseOut.next(); }
	onMouseOut = new Subject<void>();


	@HostListener('contextmenu', ['$event'])
	onRightClick(event: MouseEvent) {
		event.preventDefault();
		event.stopPropagation();
		if (!this.filterable) return;
		this.listView.editColumnFilter = undefined;
		setTimeout(() => { this.listView.editColumnFilter = this; });
	}

	getPositions() {
		return this.elm.nativeElement.getBoundingClientRect();
	}

	getSortIcon(): string {
		return this.listView.getSortIcon(this);
	}
}

@Component({
	selector: 'list-view > filter-selector',
	template: '<div><input #contains type="text" placeholder="{{ \'list-view.filter.contains\' | translate }}" [(ngModel)]="containsFilter" (keydown.enter)="setFilter()" /></div>' +
		'<div><button small [attr.ghost]="existingFilter ? null : true" [attr.reject]="existingFilter ? true : null" (click)="clearFilter()"><icon small type="filter-slash"></icon>{{ \'list-view.filter.clear-filter\' | translate }}</button>' +
		'<button small confirm (click)="setFilter()"><icon small type="filter"></icon>{{ \'list-view.filter.apply-filter\' | translate }}</button></div>',
	imports: [FormsModule, ButtonComponent, IconComponent, TranslatePipe]
})
export class ListFilterSelectorComponent implements OnDestroy, AfterViewInit {

	@Output() filterChanged = new EventEmitter<ListViewFilterOption | undefined>();

	@ViewChild('contains') containsElement!: ElementRef<HTMLElement>;

	@HostBinding('style.left.px') offsetLeft = 0;
	@HostBinding('style.top.px') offsetTop = 0;

	mouseoverElement = false;

	@HostListener('mouseover')
	onMouseOver() {
		this.mouseoverElement = true;
	}

	@HostListener('mouseout')
	onMouseOut() {
		this.mouseoverElement = false;
		this.mouseOutCloseSelector();
	}

	columnType = 'string';
	containsFilter: FilterValue = '';
	existingFilter?: ListViewFilter;

	private subscription?: Subscription;

	constructor(private listView: ListViewComponent) {
		this.offsetLeft = listView.editColumnFilter?.getPositions().left ?? 0;
		this.offsetTop = (listView.editColumnFilter?.getPositions().top ?? 0) + (listView.editColumnFilter?.getPositions().height ?? 0) - 3;
		if (listView.editColumnFilter) this.columnType = listView.getColumnType(listView.editColumnFilter);
		this.existingFilter = listView.filters.find(f => f.column == listView.editColumnFilter);
		if (this.existingFilter) {
			if (this.existingFilter.filter.filterType == 'contains' && this.existingFilter.filter.keyword)
				this.containsFilter = this.existingFilter.filter.keyword;
		}

		if (this.listView.editColumnFilter) {
			this.subscription = this.listView.editColumnFilter.onMouseOut.subscribe(() => {
				this.mouseOutCloseSelector();
			});
		}
	}
	ngAfterViewInit(): void {
		this.containsElement.nativeElement.focus();
	}

	ngOnDestroy() {
		if (this.subscription) this.subscription.unsubscribe();
	}

	mouseOutCloseSelector() {
		setTimeout(() => {
			if (this.listView.editColumnFilter?.mouseoverElement || this.mouseoverElement) return;
			this.closeSelector();
		}, 500);
	}

	closeSelector() {
		this.listView.editColumnFilter = undefined;
	}

	clearFilter() {
		this.filterChanged.emit(undefined);
	}

	setFilter() {
		if (this.containsFilter != '')
			this.filterChanged.emit(new ListViewFilterOption('contains', this.containsFilter));
		else
			this.clearFilter();
	}

}

@Component({
	selector: 'list-view',
	templateUrl: 'list-view.component.html',
	styleUrls: ['list-view.component.scss'],
	imports: [CommonModule, TranslatePipe, ListHeaderComponent, ListFilterSelectorComponent, ButtonComponent, IconComponent]
})
export class ListViewComponent {

	@HostBinding('class.sub-list')
	@Input() parentList?: ListViewComponent;
	get subList() { return this.parentList != undefined; }
	@Input() emptyLabel: string = 'general.list.empty';
	@Input() selectable = true;
	@Input() referenceName?: string;
	@Input() displayStatusColumn = false;

	@Output() rowSelected = new EventEmitter<ListRowComponent>();

	selectedRow?: ListRowComponent;
	sortingIndex: ListViewSortingIndex[] = [];
	filters: ListViewFilter[] = [];
	brandFilter: Record<string, boolean> = {};
	executeSort: (() => void)[] = [];
	listRowReferences = new Map<object, ListRowComponent>();
	empty = true;
	hasActionColumn = false;
	listData?: { sortedObjects?: object[]; };
	editColumnFilter?: ListHeaderComponent;

	private readonly languageService = inject(LanguageService);
	@ContentChildren(forwardRef(() => ListHeaderComponent)) headers!: QueryList<ListHeaderComponent>;

	private listItems: (ListItemComponent | ListRowComponent)[] = [];

	registerRow(objectReference: object, row: ListRowComponent) {
		this.listRowReferences.set(objectReference, row);
	}
	registerRowWithNoDataBinding(item: ListItemComponent | ListRowComponent) {
		if (item instanceof ListRowComponent) item.order = 0;
		this.listItems.push(item);
		this.empty = this.listItems.length === 0;
	}
	unregisterRowWithNoDataBinding(item: ListItemComponent | ListRowComponent) {
		const index = this.listItems.indexOf(item);
		if (index >= 0) this.listItems.splice(index, 1);
		this.empty = this.listItems.length === 0;
	}

	selectRow(row: ListRowComponent) {
		if (!this.selectable) return;
		if (this.selectedRow) this.selectedRow.highlighted = false;
		row.highlighted = true;
		this.selectedRow = row;
	}

	brands: BrandModel[] = [];
	addBrand(brand: BrandModel) : void {
		if (this.parentList) return this.parentList.addBrand(brand); // Add brand to the top level list. It's irrelevant for sublists
		if (brand.id && !this.brands.find(b => b.id == brand.id)) {
			this.brands.push(brand);
			this.brands.sort((b1, b2) => b1.name.localeCompare(b2.name));
			this.brandFilter[brand.shortName] = localStorage.getItem('brand-filter.' + brand.shortName) != 'false';
		}
	}

	getColumnType(column: ListHeaderComponent) {
		const index = this.getColumnIndex(column);
		let columnType = 'string';
		const foundTypes: string[] = [];
		for (let i = 0; i < 2; i++) {
			if (this.listData?.sortedObjects && this.listData?.sortedObjects?.length >= i) {
				const item = this.listData.sortedObjects[i];
				if (!item) continue;
				const row = this.listRowReferences.get(item);
				if (!row) continue;
				const value = row.getSortValue(index);
				if (value != undefined) {
					let type: string = typeof value;
					if (type == 'object') type = value instanceof Date ? 'date' : 'string';
					if (foundTypes.indexOf(type) === -1) foundTypes.push(type);
				}
			}
		}
		if (foundTypes.length == 1) columnType = foundTypes[0];
		return columnType;
	}

	getColumnIndex(column: ListHeaderComponent) { return this.headers ? this.headers.toArray().indexOf(column) : -1; }

	setSortingColumn(column: ListHeaderComponent, clearSortingIndex: boolean, direction?: ListViewSorting, noRetry = false) {

		const index = this.getColumnIndex(column);
		const sortingOptions = { direction: direction ? direction : ListViewSorting.Ascending, clearSortingIndex: clearSortingIndex };
		if (index === undefined || index < 0) {
			if (noRetry) 
				console.error('Column not found in list view, unable to set sorting');
			else
				setTimeout(() => this.setSortingColumn(column, clearSortingIndex, direction, true), 0);
			return;
		}

		let newSorting = new ListViewSortingIndex(index, sortingOptions, column);
		const existingSortingPriority = this.sortingIndex.findIndex(si => si.column === column);
		if (existingSortingPriority >= 0) {
			// If sorting column already exists in index, remove it first, and reverse direction if it's the latest one
			newSorting = this.sortingIndex.splice(existingSortingPriority, 1)[0];
			if (existingSortingPriority === 0) newSorting.options.direction = newSorting.options.direction === ListViewSorting.Ascending ? ListViewSorting.Descending : ListViewSorting.Ascending;
		}

		if (sortingOptions.clearSortingIndex) this.sortingIndex = [];
		this.sortingIndex.unshift(newSorting);

		this.sort();
	}

	sortObjectList(list: object[], rowReferences: Map<object, ListRowComponent>) {
		if (!this.sortingIndex.length) return;

		list.sort((itemA, itemB) => {
			const rowA = rowReferences.get(itemA), rowB = rowReferences.get(itemB);
			if (!rowA || !rowB) throw new Error('Unable to compare list values because list view was not well formed');

			if (rowA.groupBy || rowB.groupBy) {
				const order = this.numberAndTextCompare(rowA.groupBy ?? '', rowB.groupBy ?? '');
				if (order !== 0) return order;
			}
			if (rowA.subGroupBy || rowB.subGroupBy) {
				const order = this.numberAndTextCompare(rowA.subGroupBy ?? '', rowB.subGroupBy ?? '');
				if (order !== 0) return order;
			}

			for (const sorting of this.sortingIndex) {
				if (sorting.column) sorting.index = this.getColumnIndex(sorting.column);
				if (sorting.index < 0) continue;

				const valueA = rowA.getSortValue(sorting.index);
				const valueB = rowB.getSortValue(sorting.index);

				const order = this.numberAndTextCompare(valueA, valueB);
				if (order !== 0) return sorting.options.direction === ListViewSorting.Descending ? -order : order;
			}
			return 0;
		});
	}

	resetFilters() {
		this.filters = [];
		this.sort();
	}
	sort() {
		for (const sortMethod of this.executeSort) sortMethod();
	}

	setFilterColumn(column: ListHeaderComponent, filter?: ListViewFilterOption, noRetry = false) {
		const index = this.getColumnIndex(column);
		if (index === undefined || index < 0) {
			if (noRetry) 
				console.error('Column not found in list view, unable to set filter');
			else
				setTimeout(() => this.setFilterColumn(column, filter, true), 0);
			return;
		}
		const existingFilter = this.filters.find(f => f.column === column);
		if (existingFilter && !filter) // Remove filter
			this.filters.splice(this.filters.indexOf(existingFilter), 1);
		else if (existingFilter && filter) // Update filter
			existingFilter.filter = filter;
		else if (filter) // Add filter
			this.filters.push(new ListViewFilter(index, column, filter));

		this.sort();
	}

	updateFilteredList(list: object[], rowReferences: Map<object, ListRowComponent>) {
		listLoop: for (let i = 0; i < list.length; i++) {
			const item = list[i];
			const row = rowReferences.get(item);
			if (!row) continue;
			row.hiddenByFilter = false;

			for (const brand in this.brandFilter) {
				if (this.brandFilter[brand] === false && row.brand?.shortName == brand) {
					row.hiddenByFilter = true;
					continue listLoop;
				}
			}
			for (const filter of this.filters) {
				const value = row.getDisplayedValue(filter.index);
				const filterFunction = filter.filter.getFunction;
				if (!filterFunction) continue;

				if (!filterFunction(value)) {
					row.hiddenByFilter = true;
					continue listLoop;
				}
			}
		}
	}

	toggleBrandFilter(brandName: string) {
		this.brandFilter[brandName] = !this.brandFilter[brandName];
		localStorage.setItem('brand-filter.' + brandName, this.brandFilter[brandName] ? 'true' : 'false');
		this.sort();
	}
	showBrandFilters() {
		if (this.brands.length > 1) return true;
		for (const brand in this.brandFilter)
			if (this.brandFilter[brand] === false) return true; // If there's only one brand, but it's disabled, display the filter anyway
		return false;
	}

	numberAndTextCompare(a: string | number, b: string | number): number {
		const numberA = Number(a), numberB = Number(b);
		if (!isNaN(numberA) && !isNaN(numberB)) return numberA - numberB;
		if (isNaN(numberA) && isNaN(numberB)) return (a as string).localeCompare(b as string, this.languageService.isoCode);
		return isNaN(numberA) ? 1 : -1; // Always sort numbers before strings
	}

	getSortIcon(column: ListHeaderComponent): string {
		const index = this.sortingIndex.findIndex(i => i.column === column);
		if (!this.sortingIndex.length || index < 0) return 'fas fa-angle-down hidden';

		if (index === 0) return this.sortingIndex[index].options.direction === ListViewSorting.Descending ? 'fas fa-angle-down' : 'fas fa-angle-up';
		return this.sortingIndex[index].options.direction === ListViewSorting.Descending ? 'fal fa-angle-down' : 'fal fa-angle-up';
	}

	getFirstRowReference<T>(): T | undefined {
		if (!this.listData?.sortedObjects) return;
		return this.listData.sortedObjects[0] as T;
	}

	getNextRowReference<T>(steps: number): T | undefined {
		if (!this.listData?.sortedObjects) return;
		if (!this.selectedRow) return this.getFirstRowReference<T>();
		let refFromSelectedRow = this.getListRowReferenceByRow(this.selectedRow!);
		if (!refFromSelectedRow) return;
		let currentListViewRowIndex = this.listData.sortedObjects.findIndex(ref => ref == refFromSelectedRow!);
		if (currentListViewRowIndex == -1) return;
		let nextListViewRowReference = this.listData.sortedObjects.length >= currentListViewRowIndex + steps ? this.listData.sortedObjects[currentListViewRowIndex + steps] : undefined;
		return nextListViewRowReference as T;
	}

	selectNextRow() {
		let listViewRowToSelect: ListRowComponent | undefined;
		let steps = 0;
		do {
			const nextListViewRowReference = this.getNextRowReference(++steps);
			if (!nextListViewRowReference) return;
			listViewRowToSelect = this.listRowReferences.get(nextListViewRowReference);
			if (!listViewRowToSelect) return;
		} while (listViewRowToSelect?.hiddenByFilter);
		listViewRowToSelect?.onClick();
	}

	getListRowReferenceByRow(row: ListRowComponent): object | undefined {
		for (let [key, value] of this.listRowReferences.entries()) {
			if (value === row)
				return key;
		}
	}

	maxRowColumns = 0;
	registerColumns(columns: QueryList<ListColumnComponent>) {
		let columnCount = columns.length;
		if (columns.get(columns.length - 1)?.isActionColumn()) {
			columnCount--;
			this.hasActionColumn = true;
		}
		this.maxRowColumns = Math.max(this.maxRowColumns, columnCount);
	}

	maxColumns() {
		return Math.max(this.maxRowColumns, this.headers.length);
	}

	updateFilter(filter: ListViewFilterOption | undefined) {
		if (this.editColumnFilter) this.setFilterColumn(this.editColumnFilter, filter);
		this.editColumnFilter = undefined;
	}

	public getFilteredListData() {
		return Array.from(this.listRowReferences.entries()).filter(([listDataObject, row]) => !row.hiddenByFilter).map(([listDataObject, row]) => listDataObject);
	}
}

export class ListViewSortingIndex {
	index: number;
	column?: ListHeaderComponent;
	options: IListViewSortingOptions;
	constructor(index: number, options: any, column?: ListHeaderComponent) {
		this.index = index;
		this.column = column;
		this.options = { direction: ListViewSorting.Ascending };
		this.options = Object.assign(this.options, options);
	}
}

export class ListViewFilter {
	index: number;
	column: ListHeaderComponent;
	filter: ListViewFilterOption;
	constructor(index: number, column: ListHeaderComponent, filter: ListViewFilterOption) {
		this.index = index;
		this.column = column;
		this.filter = filter;
	}
}

export type FilterType = 'contains';
export type FilterValue = string | number | Date;

export class ListViewFilterOption {
	filterType: FilterType;
	keyword?: FilterValue;
	constructor(filterType: FilterType, keyword?: FilterValue) {
		this.filterType = filterType;
		this.keyword = keyword;
	}

	get getFunction() {
		if (this.filterType == 'contains')
			return (value: string) => { return value.toLowerCase().indexOf(this.keyword!.toString().toLowerCase()) != -1; };
		return undefined;
	}
}

@Component({
	selector: 'list-view row > column', template: '<ng-content></ng-content>'
})
export class ListColumnComponent {
	constructor(private element: ElementRef<HTMLElement>) { }

	@Input() sortValue?: any;

	getDisplayedValue() {
		return this.element.nativeElement.textContent ?? '';
	}

	getSortValue() {
		return this.sortValue !== undefined ? this.sortValue : this.getDisplayedValue();
	}

	isActionColumn() {
		// check if other tags than action tags is present
		const actionTags = ['button', 'icon', 'a'];
		const hasOtherTagsThanAction = Array.from(this.element.nativeElement.childNodes).some(node => {
			if (node.nodeType === Node.ELEMENT_NODE) {
				const tagName = (node as HTMLElement).tagName.toLowerCase();
				return !actionTags.includes(tagName);
			}
			return false;
		});
		const hasTextContent = this.element.nativeElement.textContent?.trim().length ?? 0 > 0;
		return !hasOtherTagsThanAction && !hasTextContent;
	}
}

@Component({
	selector: 'list-view > row-details',
	template: '<td colspan="100"><ng-content></ng-content></td>'
})
export class ListRowDetailsComponent {

	@HostBinding('class.highlighted') highlighted = false;
	@ContentChildren(ListColumnComponent) columns!: QueryList<ListColumnComponent>;

	constructor() { }
}

@Component({
	selector: 'list-view row',
	templateUrl: 'list-row.component.html',
	imports: [CommonModule, IconComponent, ListRowDetailsComponent, ListColumnComponent]
})
export class ListRowComponent implements OnDestroy {

	@HostBinding('class.clickable') @Input() clickable = true;
	@HostBinding('class.highlighted') @Input() highlighted = false;
	@HostBinding('class.hierarchy') @Input() showCollapseIcon = false;
	@Input() collapsed?: boolean;
	@Input() disabled = false;
	@Output() action = new EventEmitter<void>();
	@Output() groupHeaderAction = new EventEmitter<void>();
	@Output() collapsedChange = new EventEmitter<boolean>();
	@Output() filteredOut = new EventEmitter<void>();
	@ViewChild('row') private rowElement?: ElementRef<HTMLElement>;

	@HostBinding('style.order') order?: number;

	@Input()
	set select(value: boolean) {
		if (value ?? false) this.listView.selectRow(this);
	}

	columns!: QueryList<ListColumnComponent>;
	@ContentChildren(ListColumnComponent) set _columns(list: QueryList<ListColumnComponent>) {
		this.columns = list;
		this.listView.registerColumns(list);
	}

	private _groupBy?: string;
	@Input() set groupBy(newValue: string | undefined) {
		const oldValue = this._groupBy;
		this._groupBy = newValue;
		if (oldValue != newValue) this.listView.sort();
	}
	get groupBy() { return this._groupBy; }

	private _subGroupBy?: string;
	@Input() set subGroupBy(newValue: string | undefined) {
		const oldValue = this._subGroupBy;
		this._subGroupBy = newValue;
		if (oldValue != newValue) this.listView.sort();
	}
	get subGroupBy() { return this._subGroupBy; }

	private _statusIcon?: IconType;
	@Input() set statusIcon(iconType: IconType | undefined) {
		this._statusIcon = iconType;
		if (iconType) this.listView.displayStatusColumn = true;
	}
	get statusIcon() { return this._statusIcon ? this._statusIcon : (this.statusColor ? 'square-full' : undefined); }

	private _statusColor?: string;
	@Input()
	set statusColor(colorVarName: string | undefined) {
		this._statusColor = colorVarName ? 'var(--' + colorVarName + ')' : undefined;
		if (colorVarName !== undefined) {
			this.listView.displayStatusColumn = true;
			this.statusIcon = this.statusIcon ?? 'square-full';
		}
	}
	get statusColor() { return this._statusColor; }
	@Input() statusTitle?: string;

	private readonly brandService = inject(BrandService);
	public brand: BrandModel | null = null;
	@Input('brand')
	set brandId(brandId: string | undefined) {
		this.brand = this.brandService.getBrand(brandId) as BrandModel | null;
		if (this.brand) {
			this.listView.addBrand(this.brand);
			this.listView.displayStatusColumn = true;
		}
	}
	@HostBinding('attr.brand') get brandAttribute() { return this.brand?.shortName?.toLocaleLowerCase(); }

	private _hiddenByFilter = false;
	@HostBinding('class.hidden-by-filter')
	set hiddenByFilter(value: boolean) {
		this._hiddenByFilter = value;
		if (value) this.filteredOut.emit();
	}
	get hiddenByFilter() { return this._hiddenByFilter; }

	groupHeader = false;
	subGroupHeader = false;
	constructor(private listView: ListViewComponent, @Inject(ROW_OBJECT) @Optional() private rowObject: object | undefined) {
		if (rowObject) this.listView.registerRow(rowObject, this);
		else this.listView.registerRowWithNoDataBinding(this);
	}
	ngOnDestroy() { this.listView.unregisterRowWithNoDataBinding(this); }

	// Hides new objects in list until they have been sorted, so they don't show up wrong for a split-second
	@HostBinding('class.unsorted') get unsortedState() { return this.order === undefined; }
	@HostBinding('class.disabled') get disabledState() { return this.disabled || this.isDeleted; }
	get isDeleted() { return (this.rowObject as any)?.$deleted ? true : false; }
	@HostBinding('class.new') get isNew() { return (this.rowObject as any)?.$new ? true : false; }

	@HostListener('mouseover')
	mouseOver() { this.onMouseOver.next(); }
	onMouseOver = new Subject<void>();

	onClick() {
		if (!this.clickable || this.isDeleted) return;
		this.listView.selectRow(this);
		this.action.emit();
	}

	getSortValue(index: number) {
		const column = this.columns?.find((_, i) => i === index);
		return column ? column.getSortValue() : 0;
	}

	getDisplayedValue(index: number) {
		const column = this.columns?.find((_, i) => i === index);
		return column ? column.getDisplayedValue() : '';
	}

	toggleCollapsed(event: MouseEvent) {
		event.stopPropagation();
		this.collapsed = !this.collapsed;
		this.collapsedChange.emit(this.collapsed);
	}

	getRowElement() {
		return this.rowElement;
	}
}

@Component({
	selector: 'list-view list-item',
	template: '<row [statusIcon]="statusIcon" [clickable]="clickable" (action)="action.emit()"><column><ng-content></ng-content></column></row>',
	imports: [ListRowComponent, ListColumnComponent]
})
export class ListItemComponent implements OnDestroy {

	@Input() clickable = false;
	@Output() action = new EventEmitter<void>();
	@Input() statusIcon?: IconType;
	constructor(private listView: ListViewComponent, @Inject(ROW_OBJECT) @Optional() rowObject: object) {
		this.listView.registerRowWithNoDataBinding(this);
		this.listView.maxRowColumns = 1;
	}

	ngOnDestroy() { this.listView.unregisterRowWithNoDataBinding(this); }
}

@Component({
	selector: 'list-view > row > hover-menu',
	template: '<column><div [ngStyle]="{ \'top.px\': offset }" class="hover-menu-wrapper"><div class="hover-menu-container"><ng-content></ng-content></div></div></column>',
	imports: [CommonModule, ListColumnComponent]
})
export class ListHoverMenuComponent implements OnDestroy {

	offset = 0;
	@HostBinding('class.visible') visible = false;

	private subscription: Subscription;

	constructor(row: ListRowComponent, private element: ElementRef<HTMLElement>) {
		this.subscription = row.onMouseOver.subscribe(() => {
			const offsetParent = (this.element.nativeElement.offsetParent as HTMLElement)?.offsetParent as HTMLElement;
			const rowClientRect = this.element.nativeElement.parentElement!.getBoundingClientRect();
			this.offset = rowClientRect.top + (rowClientRect.height / 2) - (offsetParent?.getBoundingClientRect().top ?? 0);
			this.visible = false;
		});
	}

	ngOnDestroy() {
		this.subscription.unsubscribe();
	}
}

@Directive({
	selector: '[listData]'
})
export class ListViewDirective implements DoCheck {

	public listData: { objects?: object[]; sortedObjects?: object[]; };
	private originalDiffer?: IterableDiffer<object>;
	private sortedDiffer?: IterableDiffer<object>;
	private itemViews = new Map<object, EmbeddedViewRef<ListViewRowContext>>();

	@Input()
	set listDataFrom(data: object[] | undefined | null) {
		if (!data) return;
		this.listData.objects = data;
		this.itemViews = new Map<object, EmbeddedViewRef<ListViewRowContext>>();
		this.viewContainer.clear();
		this.listData.sortedObjects = [];
		this.originalDiffer = this.iterableDiffers.find(this.listData.objects).create<object>();
		this.sortedDiffer = this.iterableDiffers.find(this.listData.sortedObjects).create<object>();
	}

	constructor(
		private iterableDiffers: IterableDiffers,
		private template: TemplateRef<ListViewRowContext>,
		private viewContainer: ViewContainerRef,
		private listView: ListViewComponent) {

		this.listData = {};
		this.listView.listData = this.listData;
	}

	sort() {
		if (!this.listData.objects) return;
		const sortedObjects = [...this.listData.objects];

		// Use root list to sort and filter items, but use own listview's RowComponent references
		this.rootList?.updateFilteredList(sortedObjects, this.listView.listRowReferences);
		this.rootList?.sortObjectList(sortedObjects, this.listView.listRowReferences);

		this.updateSortingOrder(sortedObjects);
	}

	updateSortingOrder(list: Object[]) {
		let currentGroup: string | undefined;
		let currentSubGroup: string | undefined;
		for (let i = 0; i < list.length; i++) {
			const item = list[i];
			const row = this.listView.listRowReferences.get(item);
			if (!row) continue;

			row.order = i;
			if (row.hiddenByFilter) {
				row.groupHeader = false;
				row.subGroupHeader = false;
				continue;
			}
			row.groupHeader = row.groupBy !== undefined && row.groupBy != currentGroup;
			row.subGroupHeader = row.subGroupBy !== undefined && (row.groupHeader || row.subGroupBy != currentSubGroup);
			currentGroup = row.groupBy;
			currentSubGroup = row.subGroupBy;
		}
		this.listData.sortedObjects = list;
	}

	private rootList?: ListViewComponent;
	ngDoCheck() {
		if (!this.rootList) {
			// When first rendered, define the root list of this directive, and use that to handle sorting and filtering
			this.rootList = this.listView;
			while (this.rootList.parentList) this.rootList = this.rootList.parentList;
			this.rootList.executeSort.push(() => this.sort());
		}

		if (!this.listData.objects || !this.originalDiffer) return;
		const originalChanges = this.originalDiffer.diff(this.listData.objects);
		if (originalChanges) {

			originalChanges.forEachAddedItem(change => {
				const view = this.viewContainer.createEmbeddedView<ListViewRowContext>(
					this.template,
					{ $implicit: change.item, listData: this.listData, sorted: false, index: null },
					{
						injector: Injector.create([
							{ provide: ROW_OBJECT, useValue: change.item },
							{ provide: IS_SORTED, useValue: () => view.context.sorted }
						])
					}
				);
				this.itemViews.set(change.item, view);
			});
			originalChanges.forEachRemovedItem(change => {
				const view = this.itemViews.get(change.item);
				if (!view) return;
				view.destroy();
			});

			// Performs sorting immediately after new item views have been created
			setTimeout(() => { this.sort(); });
		}

		if (!this.listData.sortedObjects || !this.sortedDiffer) return;
		const sortedChanges = this.sortedDiffer.diff(this.listData.sortedObjects);
		if (!sortedChanges) return;

		this.listView.empty = this.listData.sortedObjects.length === 0;
		sortedChanges.forEachItem(change => {
			const view = this.itemViews.get(change.item);
			if (view) {
				view.context.sorted = true;
				view.context.index = change.currentIndex;
			}
			// Trying to move a view to the same index it's already located will cause weird bugs in ViewContainerRef, so only move when necessary
			if (view && change.currentIndex !== null && this.viewContainer.indexOf(view) !== change.currentIndex) {
				this.viewContainer.move(view, change.currentIndex);
			}
		});
	}
}

@Directive({
	selector: 'list-view[dynamic]'
})
export class ListViewDynamicDirective implements AfterViewInit {

	constructor(private listViewElementRef: ElementRef<HTMLElement>) { }

	ngAfterViewInit() {
		let listViewElm = this.listViewElementRef.nativeElement;
		let listScrollbarWidth = listViewElm.offsetWidth - (listViewElm.clientWidth == 0 ? listViewElm.offsetWidth : listViewElm.clientWidth);
		let listWidth = listViewElm.offsetWidth - parseFloat(window.getComputedStyle(listViewElm).paddingRight) - parseFloat(window.getComputedStyle(listViewElm).paddingLeft) - listScrollbarWidth;
		let tableWidth = listViewElm.querySelector('table')?.offsetWidth ?? listWidth;
		if (tableWidth <= listWidth || isNaN(listWidth)) return;
		// if table is wider than list, set header widths to match list width
		// deduct status column width from list width
		let statusColumnElm = listViewElm.querySelector('table header.status-column') as HTMLElement;
		let statusColumnWidth = statusColumnElm.offsetWidth - parseFloat(window.getComputedStyle(statusColumnElm).paddingRight) - parseFloat(window.getComputedStyle(statusColumnElm).paddingLeft);
		listWidth -= statusColumnWidth;
		listViewElm.querySelectorAll('table header:not(.status-column)').forEach((header: any) => {
			let headerWidth = header.offsetWidth - parseFloat(window.getComputedStyle(header).paddingRight) - parseFloat(window.getComputedStyle(header).paddingLeft);
			let headerWidthPercent = headerWidth / tableWidth * 100;
			let newWidth = listWidth / 100 * headerWidthPercent;
			newWidth = newWidth < 50 ? 50 : newWidth;
			header.style.width = Math.floor(newWidth) + 'px';
			let sortIconElm = header.querySelector('.sort-icon');
			let sortIconWidth = sortIconElm.offsetWidth + parseFloat(window.getComputedStyle(sortIconElm).paddingRight) + parseFloat(window.getComputedStyle(sortIconElm).paddingLeft);
			header.querySelector('.header-delimiter').style.width = Math.floor(newWidth - sortIconWidth) + 'px';
			header.title = header.innerText;
		});
	}

}

export enum ListViewSorting {
	None,
	Ascending,
	Descending
}
export interface IListViewSortingOptions {
	direction?: ListViewSorting;
	clearSortingIndex?: boolean;
}
